import { useEffect, useMemo, useState, type RefObject } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  lifecycleLabels,
  taskPriorities,
  taskTypeLabels,
  type LifecycleKey,
  type TaskPriority,
  type TaskType,
} from '@brimo/shared';
import {
  Button,
  Combobox,
  Eyebrow,
  FieldGroup,
  FilterChip,
  FormRow,
  Popover,
  TextField,
  type ComboboxOption,
} from '../../ds/components';
import { api } from '../../lib/api';
import { queryRoots } from '../../lib/events';
import { longDay } from '../tasks/dates';
import type { QueueFilter } from './api';
import styles from './QueueFilterPopover.module.css';

/** Task types offered by „Eigener Filter" (the types a queue can hold). */
const FILTER_TYPES: readonly TaskType[] = [
  'call',
  'follow_up',
  'email',
  'whatsapp',
  'document',
  'review',
  'chase',
  'review_documents',
  'appointment_outcome',
  'approval',
  'manual',
];

const LIFECYCLES: readonly LifecycleKey[] = [
  'new',
  'contacted',
  'call_booked',
  'prospect',
  'follow_up',
  'customer',
  'existing_customer',
  'no_show',
  'not_reached',
];

export interface FilterNames {
  contact?: string | undefined;
  property?: string | undefined;
}

/** „Objekt: Donaulände 9 · Typ: Anruf" — the field value of Queue 01/02 („keiner" without a filter). */
export function describeFilter(filter: QueueFilter | null, names: FilterNames): string {
  if (filter === null) return 'keiner';
  const parts: string[] = [];
  if (filter.propertyId) parts.push(`Objekt: ${names.property ?? 'gewählt'}`);
  if (filter.contactId) parts.push(`Kontakt: ${names.contact ?? 'gewählt'}`);
  if (filter.types && filter.types.length > 0)
    parts.push(`Typ: ${filter.types.map((t) => taskTypeLabels[t]).join(', ')}`);
  if (filter.priorities && filter.priorities.length > 0) {
    parts.push(`Priorität: ${filter.priorities.map((p) => p.toUpperCase()).join(', ')}`);
  }
  if (filter.dueFrom || filter.dueTo) {
    parts.push(
      `Fällig: ${filter.dueFrom ? longDay(filter.dueFrom) : '…'} – ${filter.dueTo ? longDay(filter.dueTo) : '…'}`,
    );
  }
  if (filter.lifecycle && filter.lifecycle.length > 0) {
    parts.push(
      `Lifecycle: ${filter.lifecycle.map((key) => lifecycleLabels[key as LifecycleKey] ?? key).join(', ')}`,
    );
  }
  return parts.length > 0 ? parts.join(' · ') : 'keiner';
}

function isEmptyFilter(filter: QueueFilter): boolean {
  return (
    !(filter.types && filter.types.length > 0) &&
    !(filter.priorities && filter.priorities.length > 0) &&
    !filter.dueFrom &&
    !filter.dueTo &&
    !(filter.lifecycle && filter.lifecycle.length > 0) &&
    !filter.propertyId &&
    !filter.contactId
  );
}

interface ContactHit {
  id: string;
  firstName: string;
  lastName: string;
  lifecycle: { label: string };
}

interface SearchHit {
  id: string;
  type: string;
  title: string;
  subtitle: string | null;
  locked: boolean;
}

function useContactOptions(query: string) {
  return useQuery({
    queryKey: [...queryRoots.contacts, 'picker', query],
    queryFn: () => api<{ items: ContactHit[] }>(`/contacts?q=${encodeURIComponent(query)}&limit=8`),
    enabled: query.trim().length >= 2,
    staleTime: 30_000,
  });
}

function usePropertyOptions(query: string) {
  return useQuery({
    queryKey: ['search', 'properties', query],
    queryFn: () =>
      api<{ groups: Array<{ type: string; hits: SearchHit[] }> }>(
        `/search?q=${encodeURIComponent(query)}&limit=8`,
      ),
    enabled: query.trim().length >= 2,
    staleTime: 30_000,
  });
}

export interface QueueFilterPopoverProps {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  value: QueueFilter | null;
  names: FilterNames;
  onClose: () => void;
  onApply: (filter: QueueFilter | null, names: FilterNames) => void;
}

/** The „Eigener Filter" editor of Queue 01/02: AND-combined restrictions on the own open tasks. */
export function QueueFilterPopover({
  open,
  anchorRef,
  value,
  names,
  onClose,
  onApply,
}: QueueFilterPopoverProps) {
  const [types, setTypes] = useState<TaskType[]>([]);
  const [priorities, setPriorities] = useState<TaskPriority[]>([]);
  const [dueFrom, setDueFrom] = useState('');
  const [dueTo, setDueTo] = useState('');
  const [lifecycle, setLifecycle] = useState<string[]>([]);
  const [contactId, setContactId] = useState<string | null>(null);
  const [contactName, setContactName] = useState<string | undefined>(undefined);
  const [contactQuery, setContactQuery] = useState('');
  const [propertyId, setPropertyId] = useState<string | null>(null);
  const [propertyName, setPropertyName] = useState<string | undefined>(undefined);
  const [propertyQuery, setPropertyQuery] = useState('');
  const contacts = useContactOptions(contactQuery);
  const properties = usePropertyOptions(propertyQuery);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!open) return;
    // The panel is taller than the room under the field on small viewports: cap it at the larger side
    // (the Popover flips to that side) so the footer stays reachable.
    const rect = anchorRef.current?.getBoundingClientRect();
    if (rect) setMaxHeight(Math.max(window.innerHeight - rect.bottom, rect.top) - 6 - 8);
    setTypes(value?.types ?? []);
    setPriorities(value?.priorities ?? []);
    setDueFrom(value?.dueFrom ?? '');
    setDueTo(value?.dueTo ?? '');
    setLifecycle(value?.lifecycle ?? []);
    setContactId(value?.contactId ?? null);
    setContactName(names.contact);
    setPropertyId(value?.propertyId ?? null);
    setPropertyName(names.property);
    setContactQuery('');
    setPropertyQuery('');
  }, [open, value, names, anchorRef]);

  const contactOptions = useMemo<ComboboxOption[]>(() => {
    const options: ComboboxOption[] = (contacts.data?.items ?? []).map((hit) => ({
      id: hit.id,
      label: `${hit.firstName} ${hit.lastName}`.trim(),
      description: hit.lifecycle.label,
    }));
    if (contactId !== null && contactName !== undefined && !options.some((o) => o.id === contactId)) {
      options.unshift({ id: contactId, label: contactName });
    }
    return options;
  }, [contacts.data, contactId, contactName]);

  const propertyOptions = useMemo<ComboboxOption[]>(() => {
    const group = properties.data?.groups.find((entry) => entry.type === 'property');
    const options: ComboboxOption[] = (group?.hits ?? [])
      .filter((hit) => !hit.locked)
      .map((hit) => ({ id: hit.id, label: hit.title, description: hit.subtitle ?? undefined }));
    if (propertyId !== null && propertyName !== undefined && !options.some((o) => o.id === propertyId)) {
      options.unshift({ id: propertyId, label: propertyName });
    }
    return options;
  }, [properties.data, propertyId, propertyName]);

  function toggle<T>(list: T[], item: T): T[] {
    return list.includes(item) ? list.filter((entry) => entry !== item) : [...list, item];
  }

  function apply() {
    const filter: QueueFilter = {};
    if (types.length > 0) filter.types = types;
    if (priorities.length > 0) filter.priorities = priorities;
    if (dueFrom !== '') filter.dueFrom = dueFrom;
    if (dueTo !== '') filter.dueTo = dueTo;
    if (lifecycle.length > 0) filter.lifecycle = lifecycle;
    if (contactId !== null) filter.contactId = contactId;
    if (propertyId !== null) filter.propertyId = propertyId;
    onApply(isEmptyFilter(filter) ? null : filter, { contact: contactName, property: propertyName });
    onClose();
  }

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorRef={anchorRef}
      placement="bottom-start"
      offset={6}
      width={520}
      variant="panel"
      label="Eigener Filter"
      className={styles.panel}
      style={maxHeight === undefined ? undefined : { maxHeight }}
    >
      <div className={styles.body}>
        <Eyebrow size={11}>Eigener Filter</Eyebrow>
        <FieldGroup legend="Typ">
          <div className={styles.chips}>
            {FILTER_TYPES.map((type) => (
              <FilterChip
                key={type}
                label={taskTypeLabels[type]}
                active={types.includes(type)}
                onClick={() => setTypes((list) => toggle(list, type))}
              />
            ))}
          </div>
        </FieldGroup>
        <FieldGroup legend="Priorität">
          <div className={styles.chips}>
            {taskPriorities.map((priority) => (
              <FilterChip
                key={priority}
                label={priority.toUpperCase()}
                active={priorities.includes(priority)}
                onClick={() => setPriorities((list) => toggle(list, priority))}
              />
            ))}
          </div>
        </FieldGroup>
        <FormRow columns={2}>
          <TextField
            label="Fällig von"
            type="date"
            value={dueFrom}
            onChange={(event) => setDueFrom(event.target.value)}
          />
          <TextField
            label="Fällig bis"
            type="date"
            value={dueTo}
            onChange={(event) => setDueTo(event.target.value)}
          />
        </FormRow>
        <FieldGroup legend="Lifecycle">
          <div className={styles.chips}>
            {LIFECYCLES.map((key) => (
              <FilterChip
                key={key}
                label={lifecycleLabels[key]}
                active={lifecycle.includes(key)}
                onClick={() => setLifecycle((list) => toggle(list, key))}
              />
            ))}
          </div>
        </FieldGroup>
        <FormRow columns={2}>
          <Combobox
            label="Kontakt"
            placeholder="Name suchen"
            options={contactOptions}
            value={contactId}
            filter={false}
            emptyText={contactQuery.trim().length < 2 ? 'Mindestens zwei Zeichen eingeben' : 'Keine Treffer'}
            onQueryChange={setContactQuery}
            onChange={(id, option) => {
              setContactId(id);
              setContactName(option?.label);
            }}
          />
          <Combobox
            label="Objekt"
            placeholder="Objekt suchen"
            options={propertyOptions}
            value={propertyId}
            filter={false}
            emptyText={propertyQuery.trim().length < 2 ? 'Mindestens zwei Zeichen eingeben' : 'Keine Treffer'}
            onQueryChange={setPropertyQuery}
            onChange={(id, option) => {
              setPropertyId(id);
              setPropertyName(option?.label);
            }}
          />
        </FormRow>
        <div className={styles.footer}>
          <Button
            variant="secondary"
            onClick={() => {
              onApply(null, {});
              onClose();
            }}
          >
            Filter entfernen
          </Button>
          <Button onClick={apply}>Übernehmen</Button>
        </div>
      </div>
    </Popover>
  );
}
